<template>
  <AIAssistantBaseChatResponse :loading="loading">
    <div class="tw:grid tw:gap-4">
      <AIAssistantBaseChatType
        :delay="chatDelay"
        :disabled="chatTypeComplete"
        html
        @complete="handleChatTypeComplete"
      >
        <template v-if="chatState === 'moment'">
          {{ getLocaleText("ai_assistant_panel.just_a_moment") }}
        </template>
        <i18n-t
          v-else
          keypath="ai_suggested_contacts_panel.chat_result_with_link"
          scope="global"
        >
          <template #link>
            <a
              class="tw:cursor-pointer tw:text-blue-700 hover:tw:underline"
              data-testid="name"
              @click="handleSelectedClick"
            >
              {{ selectedName }}
            </a>
          </template>
          <template #type>
            <span>
              {{ typeLabel }}
            </span>
          </template>
        </i18n-t>
      </AIAssistantBaseChatType>

      <AIAssistantBaseChatTransition
        v-if="showResultsList"
        appear
        class="tw:-mx-4"
      >
        <AISuggestedContactsTags
          v-model:contact-type="contactType"
          data-index="0"
          :loading="loading"
        />
        <AISuggestedContactsSelection
          v-model:selected-ids="selectedIds"
          v-model:sort-direction="sortDirection"
          v-model:sort-field="sortField"
          :contacts="contacts"
          data-index="1"
          :loading="loading"
        />
      </AIAssistantBaseChatTransition>
    </div>
  </AIAssistantBaseChatResponse>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseChatResponse from "shared/components/AI/AIAssistant/AIAssistantBaseChatResponse.vue";
import AIAssistantBaseChatTransition from "shared/components/AI/AIAssistant/AIAssistantBaseChatTransition.vue";
import AIAssistantBaseChatType from "shared/components/AI/AIAssistant/AIAssistantBaseChatType.vue";
import AISuggestedContactsSelection from "shared/components/AI/AISuggestedContacts/AISuggestedContactsSelection.vue";
import AISuggestedContactsTags from "shared/components/AI/AISuggestedContacts/AISuggestedContactsTags.vue";
import { type SuggestedContactOptions } from "shared/composables/useSuggestedContacts";
import { safePush } from "shared/helpers/routing";
import {
  type Contact,
  type ContactList,
  type MediaRelease,
} from "shared/resources";
import { type Nullable } from "shared/types/utility";

export interface AISuggestedContactsListResponseProps {
  chatDelay?: number;
  chatState?: "moment" | "result";
  contactList?: Nullable<ContactList>;
  contacts: Contact[];
  loading?: boolean;
  mediaRelease?: Nullable<MediaRelease>;
}

const selectedIds = defineModel<string[]>("selectedIds", { default: () => [] });

const contactType = defineModel<SuggestedContactOptions["contactType"]>(
  "contactType",
  {
    default: "influencer",
  }
);

const selectedType = computed<string>(() => {
  if (props.mediaRelease) {
    return props.mediaRelease.mediaReleaseType || "media_release";
  }

  return "list";
});

const typeLabel = computed<string>(() =>
  getLocaleText(`ai_suggested_contacts_panel.${selectedType.value}`)
);

const sortDirection = defineModel<SuggestedContactOptions["sortDirection"]>(
  "sortDirection",
  {
    default: "desc",
  }
);

const sortField = defineModel<SuggestedContactOptions["sortField"]>(
  "sortField",
  {
    default: "relevance",
  }
);

const props = defineProps<AISuggestedContactsListResponseProps>();

const emit = defineEmits<{ chatTypeComplete: [] }>();

const router = useRouter();
const safeRouterPush = safePush({ router });

const chatTypeComplete = ref<boolean>(false);

const showResultsList = computed<boolean>(
  () =>
    chatTypeComplete.value && (!props.chatState || props.chatState === "result")
);

const selectedName = computed<string>(() => {
  if (props.contactList) {
    return props.contactList.name;
  }

  if (props.mediaRelease) {
    return props.mediaRelease.name;
  }

  return "";
});

function handleChatTypeComplete() {
  chatTypeComplete.value = true;
  emit("chatTypeComplete");
}

function handleSelectedClick() {
  if (props.contactList) {
    safeRouterPush({
      name: "outreach-lists-contacts",
      params: { listId: props.contactList.id },
      query: { listLabel: props.contactList.name },
    });

    return;
  }

  if (props.mediaRelease) {
    const name =
      props.mediaRelease.mediaReleaseType === "pitch"
        ? "pitch-edit"
        : "media-release-edit";

    safeRouterPush({
      name,
      params: { id: props.mediaRelease.id },
    });
  }
}

watch(
  () => props.chatState,
  () => {
    chatTypeComplete.value = false;
  }
);
</script>
